import { type SupportedCountry, validatePostalCode, validateState } from "@/lib/validation";

/** Structured address shape used by the AddressEditor component and
 *  the LocationAddress JSONB on the backend. Keyed index signature
 *  makes the type assignable to the SDK's free-form LocationAddress. */
export interface AddressValue {
  line1: string;
  line2: string;
  city: string;
  state: string;
  postal_code: string;
  country: SupportedCountry;
  [key: string]: unknown;
}

export const EMPTY_ADDRESS: AddressValue = {
  line1: "",
  line2: "",
  city: "",
  state: "",
  postal_code: "",
  country: "US",
};

/** Summary-line formatter for places that show an address read-only
 *  (e.g. org list card). Graceful on partial values. */
export function formatAddressOneLine(a: AddressValue | null | undefined): string {
  if (!a) return "";
  const street = [a.line1, a.line2].filter(Boolean).join(", ");
  const cityLine = [a.city, [a.state, a.postal_code].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ");
  return [street, cityLine, a.country].filter(Boolean).join(", ");
}

/** Does this address have all required fields filled and pass format
 *  validation? */
export function isAddressValid(a: AddressValue): boolean {
  return (
    a.line1.trim().length > 0 &&
    a.city.trim().length > 0 &&
    validateState(a.state, a.country, { required: true }).ok &&
    validatePostalCode(a.postal_code, a.country, { required: true }).ok
  );
}

/**
 * Pre-fill an AddressValue from whatever the backend returned. Handles
 * the shape mismatch between `address: string` (org) and
 * `address_json: { line1, city, ... }` (location).
 */
export function addressFromAny(
  line: string | null | undefined,
  json: Record<string, unknown> | null | undefined,
): AddressValue {
  const j = json ?? {};
  const hasStruct = typeof j.line1 === "string" || typeof j.city === "string";
  if (hasStruct) {
    const country = typeof j.country === "string" ? j.country.toUpperCase() : "";
    return {
      line1: typeof j.line1 === "string" ? j.line1 : "",
      line2: typeof j.line2 === "string" ? j.line2 : "",
      city: typeof j.city === "string" ? j.city : "",
      state: typeof j.state === "string" ? j.state : "",
      postal_code: typeof j.postal_code === "string" ? j.postal_code : "",
      country: country === "CA" ? "CA" : "US",
    };
  }
  // Fallback: stuff the free-text line into line1 so the user doesn't
  // lose data on first save. State / postal will be empty until they
  // re-enter them.
  return { ...EMPTY_ADDRESS, line1: line ?? "" };
}
